import { qz } from "../client";
import { type NetworkingInfo, type NetworkingQueryInput } from "./types";

const toStringArray = (value: unknown): null | string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : null;

const toStringOrNull = (value: unknown): null | string =>
  typeof value === "string" ? value : null;

const toBoolean = (value: unknown): boolean =>
  typeof value === "boolean" ? value : false;

const toNetworkingInfo = (value: unknown): NetworkingInfo | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const device = value as Record<string, unknown>;

  return {
    hostname: toStringOrNull(device.hostname),
    id: toStringOrNull(device.id),
    ip: toStringOrNull(device.ip),
    ip4: toStringArray(device.ip4),
    ip6: toStringArray(device.ip6),
    mac: toStringOrNull(device.mac),
    name: toStringOrNull(device.name),
    primary: toBoolean(device.primary),
    up: toBoolean(device.up),
    username: toStringOrNull(device.username),
  };
};

const getNetworkingDevice = async ({
  hostname,
  port,
}: NetworkingQueryInput = {}): Promise<NetworkingInfo | null> => {
  const result = await qz.networking.device(hostname, port);
  return toNetworkingInfo(result);
};

const getNetworkingDevices = async ({
  hostname,
  port,
}: NetworkingQueryInput = {}): Promise<NetworkingInfo[]> => {
  const result = await qz.networking.devices(hostname, port);
  return Array.isArray(result)
    ? result.flatMap((item) => {
        const parsed = toNetworkingInfo(item);
        return parsed ? [parsed] : [];
      })
    : [];
};

const getNetworkingHostname = async (
  hostname?: string,
  port?: number,
): Promise<string> =>
  qz.networking.hostname(
    hostname,
    port === null || port === undefined ? undefined : String(port),
  );

export const networkingDeviceQueryOptions = (
  input: NetworkingQueryInput = {},
) => ({
  queryFn: () => getNetworkingDevice(input),
  queryKey: [
    "qz.networking.device",
    input.hostname ?? null,
    input.port ?? null,
  ] as const,
});

export const networkingDevicesQueryOptions = (
  input: NetworkingQueryInput = {},
) => ({
  queryFn: () => getNetworkingDevices(input),
  queryKey: [
    "qz.networking.devices",
    input.hostname ?? null,
    input.port ?? null,
  ] as const,
});

export const networkingHostnameQueryOptions = (
  input: NetworkingQueryInput = {},
) => ({
  queryFn: () => getNetworkingHostname(input.hostname, input.port),
  queryKey: [
    "qz.networking.hostname",
    input.hostname ?? null,
    input.port ?? null,
  ] as const,
});
